import type { MaterialInfo, PartDefinition } from "@rogue/rackbuilder"
import { AdjustOnlyVisual } from "@/js/pages/product/rackbuilder/constants"
import { getRackModel } from "@/js/pages/product/rackbuilder/modelPaths"
import { createFMLSideShrouds, createFMLShroudCutout, getFlipX, getRotateAndFlipX } from "../fmlSharedParts"
import type { FMLContext } from "../fmlSharedParts"

// FML Parts — All part definitions for Standard FML (FML-6 and FML-HR)

// MARK: Side Pulleys

/**
 * Pulleys only — used in both full rack and half rack contexts.
 * Shrouds are handled separately so the caller decides where to place them.
 */
export function createFMLPulleysBase(materials: MaterialInfo, left: boolean): PartDefinition {
  return {
    mesh        : getRackModel('FML', 'side pulleys'),
    adjustments : getFlipX(left),
    materials,
    slots       : {
      plates: {
        mesh        : getRackModel('FML', 'side pulley plates'),
        adjustments : getFlipX(left),
        slots       : {},
      },
    },
  }
}

/**
 * Pulleys + optional shrouds — used for the full rack back crossmember slot.
 */
export function createFMLPulleysWithShrouds(
  context: FMLContext,
  left: boolean
): PartDefinition[] {
  const parts: PartDefinition[] = [createFMLPulleysBase(context.materials, left)]

  if (context.config.ftShrouds) {
    parts.push(createFMLSideShrouds(left))
    parts.push(createFMLShroudCutout(left))
  }

  return parts
}

// MARK: Front System

/**
 * FML front pulley → sharedRails → [backShared (resistance on left), FML back mesh]
 * backShared is built by FMLBuilder.buildFMLBackShared and passed in.
 */
export function createFMLFrontSystem(
  context: FMLContext,
  left: boolean,
  backShared: PartDefinition
): PartDefinition {
  const { materials, depth } = context

  const sharedRails: PartDefinition = {
    mesh  : getRackModel('FML', `bottom rail ${depth}`),
    slots : {
      back: [
        backShared,
        { mesh: getRackModel('FML', 'back'), slots: {}, materials },
      ],
    },
    anchor         : 'front',
    materials,
    adjustments    : getRotateAndFlipX(left),
    adjustmentType : AdjustOnlyVisual,
  }

  return {
    mesh        : getRackModel('FML', 'front'),
    slots       : { rail: sharedRails },
    materials,
    adjustments : getFlipX(left),
  }
}
